import React from "react";
import Typography from "@material-ui/core/Typography";

import { APP_STRINGS } from "constants/appStrings";
import { adminPortalMock } from "mocks/adminPortalMock";

import "./index.css";

const priorityClassMap = {
  Alta: "admin-portal__pill admin-portal__pill--high",
  Media: "admin-portal__pill admin-portal__pill--medium",
};

const pipelineClassMap = {
  lead: "admin-portal__pipeline-bar admin-portal__pipeline-bar--lead",
  triage: "admin-portal__pipeline-bar admin-portal__pipeline-bar--triage",
  proposal: "admin-portal__pipeline-bar admin-portal__pipeline-bar--proposal",
  won: "admin-portal__pipeline-bar admin-portal__pipeline-bar--won",
};

export default function AdminPortalPage() {
  const { summary, leads, clients, schedule, alerts, pipeline } = adminPortalMock;

  return (
    <div className="admin-portal">
      <aside className="admin-portal__sidebar">
        <div className="admin-portal__sidebar-brand">
          <span className="admin-portal__sidebar-label">Painel interno</span>
          <strong>{APP_STRINGS.brand.fullName}</strong>
          <small>Gestao comercial e operacao do escritorio</small>
        </div>

        <nav className="admin-portal__nav">
          <a href="#visao-geral" className="admin-portal__nav-item">
            Visao geral
          </a>
          <a href="#leads" className="admin-portal__nav-item">
            Leads
          </a>
          <a href="#clientes" className="admin-portal__nav-item">
            Clientes
          </a>
          <a href="#agenda" className="admin-portal__nav-item">
            Agenda
          </a>
          <a href="#alertas" className="admin-portal__nav-item">
            Alertas
          </a>
        </nav>

        <div className="admin-portal__sidebar-note">
          Rota demonstrativa de uso interno. Sem link exposto no site.
        </div>
      </aside>

      <main className="admin-portal__content">
        <header className="admin-portal__topbar">
          <div>
            <span className="admin-portal__eyebrow">Admin demo</span>
            <Typography component="h1" className="admin-portal__title">
              Central interna do escritorio
            </Typography>
            <p className="admin-portal__subtitle">
              Uma visao consolidada de leads, clientes, agenda e prioridades do
              dia, simulando um ambiente operacional para apresentacao.
            </p>
          </div>

          <div className="admin-portal__top-meta">
            <strong>28 mar 2026</strong>
            <span>Equipe online: 4 pessoas</span>
          </div>
        </header>

        <section id="visao-geral" className="admin-portal__section">
          <div className="admin-portal__section-head">
            <div>
              <span className="admin-portal__section-tag">Resumo executivo</span>
              <Typography component="h2" className="admin-portal__section-title">
                Visao geral da operacao
              </Typography>
            </div>
          </div>

          <div className="admin-portal__summary-grid">
            {summary.map((item) => (
              <article key={item.label} className="admin-portal__summary-card">
                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <p>{item.detail}</p>
              </article>
            ))}
          </div>

          <article className="admin-portal__panel">
            <div className="admin-portal__panel-head">
              <div>
                <span className="admin-portal__section-tag">Pipeline</span>
                <Typography component="h3" className="admin-portal__panel-title">
                  Funil comercial atual
                </Typography>
              </div>
            </div>

            <div className="admin-portal__pipeline">
              {pipeline.map((item) => (
                <div key={item.stage} className="admin-portal__pipeline-item">
                  <div className="admin-portal__pipeline-head">
                    <strong>{item.stage}</strong>
                    <span>{item.total}</span>
                  </div>
                  <div className={pipelineClassMap[item.color]}>
                    <div style={{ width: `${Math.min(item.total * 12, 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </article>
        </section>

        <div className="admin-portal__layout">
          <div className="admin-portal__main-column">
            <section id="leads" className="admin-portal__section">
              <article className="admin-portal__panel">
                <div className="admin-portal__panel-head">
                  <div>
                    <span className="admin-portal__section-tag">Comercial</span>
                    <Typography component="h3" className="admin-portal__panel-title">
                      Leads em acompanhamento
                    </Typography>
                  </div>
                </div>

                <div className="admin-portal__table">
                  <div className="admin-portal__table-head">
                    <span>Lead</span>
                    <span>Area</span>
                    <span>Status</span>
                    <span>Prioridade</span>
                    <span>Atualizacao</span>
                  </div>

                  {leads.map((lead) => (
                    <div key={lead.name} className="admin-portal__table-row">
                      <div>
                        <strong>{lead.name}</strong>
                        <small>{lead.source}</small>
                      </div>
                      <span>{lead.area}</span>
                      <span>{lead.status}</span>
                      <span className={priorityClassMap[lead.priority]}>{lead.priority}</span>
                      <span>{lead.updatedAt}</span>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <section id="clientes" className="admin-portal__section">
              <article className="admin-portal__panel">
                <div className="admin-portal__panel-head">
                  <div>
                    <span className="admin-portal__section-tag">Clientes</span>
                    <Typography component="h3" className="admin-portal__panel-title">
                      Casos acompanhados no portal
                    </Typography>
                  </div>
                </div>

                <div className="admin-portal__client-list">
                  {clients.map((client) => (
                    <div key={client.name} className="admin-portal__client-card">
                      <div className="admin-portal__client-head">
                        <strong>{client.name}</strong>
                        <span>{client.stage}</span>
                      </div>
                      <p>{client.case}</p>
                      <div className="admin-portal__client-meta">
                        <small>{client.advisor}</small>
                        <small>Proxima acao: {client.nextAction}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </section>
          </div>

          <div className="admin-portal__side-column">
            <section id="agenda" className="admin-portal__section">
              <article className="admin-portal__panel">
                <div className="admin-portal__panel-head">
                  <div>
                    <span className="admin-portal__section-tag">Agenda</span>
                    <Typography component="h3" className="admin-portal__panel-title">
                      Compromissos do dia
                    </Typography>
                  </div>
                </div>

                <div className="admin-portal__schedule-list">
                  {schedule.map((item) => (
                    <div key={`${item.time}-${item.person}`} className="admin-portal__schedule-card">
                      <strong>{item.time}</strong>
                      <div>
                        <span>{item.title}</span>
                        <small>{item.person}</small>
                      </div>
                      <em>{item.mode}</em>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <section id="alertas" className="admin-portal__section">
              <article className="admin-portal__panel">
                <div className="admin-portal__panel-head">
                  <div>
                    <span className="admin-portal__section-tag">Alertas</span>
                    <Typography component="h3" className="admin-portal__panel-title">
                      Pontos que exigem atencao
                    </Typography>
                  </div>
                </div>

                <div className="admin-portal__alert-list">
                  {alerts.map((item) => (
                    <div key={item.title} className="admin-portal__alert-card">
                      <strong>{item.title}</strong>
                      <p>{item.detail}</p>
                    </div>
                  ))}
                </div>
              </article>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
